import React, {useState, useEffect, useMemo} from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Table,
  Card,
  InputGroup,
  Form,
  Modal,
  Pagination,
  Dropdown,
  ButtonGroup,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaFileExcel,
  FaSearch,
  FaUsers,
  FaBuilding,
  FaUpload,
  FaDownload,
  FaCheckCircle,
} from "react-icons/fa";
import * as XLSX from "xlsx"; // Importamos la librería xlsx

const Contacts = ({contacts, setContacts, organizations}) => {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingContact, setEditingContact] = useState(null); // Estado para el contacto en edición
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formState, setFormState] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  // Componente reutilizable para las tarjetas de KPIs
  const KpiCard = ({icon, title, value, color}) => (
    <Card
      className={`shadow-sm rounded-4 border-0 border-start border-5 border-${color} h-100`}
    >
      <Card.Body className="d-flex align-items-center">
        <div className={`fs-2 text-${color} me-3`}>{icon}</div>
        <div>
          <div className="fs-4 fw-bold">{value}</div>
          <div className="text-muted text-uppercase small">{title}</div>
        </div>
      </Card.Body>
    </Card>
  );

  useEffect(() => {
    if (editingContact) {
      setFormState(editingContact);
    } else {
      setFormState({
        firstName: "",
        lastName: "",
        transportista: "",
        rut: "",
        email: "",
        phone: "",
      });
    }
  }, [editingContact, showModal]);

  // --- Funciones para el Modal ---
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingContact(null); // Limpiamos el contacto en edición al cerrar
  };

  const handleShowAddModal = () => {
    setEditingContact(null); // Nos aseguramos que no hay un contacto en edición
    setShowModal(true);
  };

  const handleShowEditModal = (contact) => {
    setEditingContact(contact); // Guardamos el contacto a editar
    setShowModal(true);
  };

  // --- Lógica para guardar/actualizar ---
  const handleSaveContact = (e) => {
    e.preventDefault();
    const updatedContact = {
      id: editingContact ? editingContact.id : `contact-${Date.now()}`,
      ...formState, // El resto de los datos del formulario
    };

    if (editingContact) {
      // Actualizar
      setContacts(
        contacts.map((c) => (c.id === editingContact.id ? updatedContact : c))
      );
    } else {
      // Crear
      setContacts([...contacts, updatedContact]);
    }

    handleCloseModal();
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (contactToDelete) {
      setContacts(contacts.filter((c) => c.id !== contactToDelete.id));
    }
    setShowDeleteModal(false);
    setContactToDelete(null);
  };

  const handleFormChange = (e) => {
    const {name, value} = e.target;
    setFormState((prev) => ({...prev, [name]: value}));
  };

  // Obtenemos una lista única de empresas para el selector del modal
  const uniqueOrganizations = useMemo(() => {
    // Ahora usamos la lista maestra de transportistas que viene por props
    return [
      ...new Set((organizations || []).map((org) => org.name).filter(Boolean)),
    ];
  }, [organizations]);

  // --- Lógica para Exportar a Excel ---
  const handleExportToExcel = () => {
    // 1. Preparamos los datos: seleccionamos y renombramos las columnas que queremos.
    const dataToExport = filteredContacts.map((contact) => ({
      Nombre: contact.firstName,
      Apellido: contact.lastName,
      Transportista: contact.transportista,
      RUT: contact.rut,
      "Correo Electrónico": contact.email,
      "Nro. Telefónico": contact.phone,
    }));

    // 2. Creamos una hoja de cálculo a partir de nuestros datos.
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    // 3. Creamos un nuevo libro de trabajo.
    const workbook = XLSX.utils.book_new();
    // 4. Añadimos la hoja de cálculo al libro, dándole un nombre ("Contactos").
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contactos");
    // 5. Generamos el archivo Excel y disparamos la descarga.
    XLSX.writeFile(workbook, "ListaDeContactos.xlsx");
  };

  // --- Lógica para Descargar Formato de Importación ---
  const handleDownloadFormat = () => {
    // 1. Definimos las cabeceras del formato.
    const headers = [["Nombre", "Apellido", "RUT", "Email", "telefono"]];
    // 2. Creamos una hoja de cálculo vacía, solo con las cabeceras.
    const worksheet = XLSX.utils.aoa_to_sheet(headers);
    // 3. Creamos un nuevo libro de trabajo.
    const workbook = XLSX.utils.book_new();
    // 4. Añadimos la hoja de cálculo al libro.
    XLSX.utils.book_append_sheet(workbook, worksheet, "Formato Contactos");
    // 5. Generamos el archivo Excel y disparamos la descarga.
    XLSX.writeFile(workbook, "Formato_Importacion_Contactos.xlsx");
  };

  // --- Lógica para Importar desde Excel (Placeholder) ---
  const handleImportClick = () => {
    // Simula el click en un input de tipo file oculto.
    document.getElementById("import-file-input").click();
  };

  // Lógica de búsqueda: filtra los contactos basándose en el término de búsqueda
  const filteredContacts = (contacts || []).filter((contact) => {
    const contactData =
      `${contact.firstName} ${contact.lastName} ${contact.transportista} ${contact.rut} ${contact.email} ${contact.phone}`.toLowerCase();
    return contactData.includes(searchTerm.toLowerCase());
  });

  // --- Efecto para reiniciar la paginación al buscar ---
  useEffect(() => {
    // Si el término de búsqueda cambia, volvemos a la página 1
    setCurrentPage(1);
  }, [searchTerm]);

  // --- Lógica de Paginación ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredContacts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPaginationItems = () => {
    const items = [];
    const maxPagesToShow = 5; // Máximo de números de página a mostrar
    const halfPagesToShow = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfPagesToShow);
    let endPage = Math.min(totalPages, currentPage + halfPagesToShow);

    if (currentPage - halfPagesToShow <= 1) {
      endPage = Math.min(totalPages, maxPagesToShow);
    }

    if (currentPage + halfPagesToShow >= totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    // Botones de navegación
    items.push(
      <Pagination.First
        key="first"
        onClick={() => paginate(1)}
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => paginate(1)}>
          {1}
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => paginate(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => paginate(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => paginate(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  return (
    <Container fluid className="p-4 bg-light">
      {/* Input oculto para la importación de archivos */}
      <input
        type="file"
        id="import-file-input"
        style={{display: "none"}}
        accept=".xlsx, .xls, .csv"
      />

      {/* Encabezado de Contactos */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="fw-bold mb-1">
                <FaUsers className="me-3 text-primary" />
                Gestión de Contactos
              </h2>
              <p className="text-muted mb-0">
                Administra la base de datos central de tus contactos
              </p>
            </div>
            <Dropdown as={ButtonGroup}>
              <Button
                variant="primary"
                size="lg"
                onClick={handleShowAddModal}
                className="rounded-start-pill px-4"
              >
                <FaPlus className="me-2" />
                Añadir Contacto
              </Button>
              <Dropdown.Toggle
                split
                variant="primary"
                size="lg"
                className="rounded-end-pill"
              />
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleImportClick}>
                  <FaUpload className="me-2" /> Importar desde Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExportToExcel}>
                  <FaFileExcel className="me-2" /> Exportar a Excel
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleDownloadFormat}>
                  <FaDownload className="me-2" /> Descargar Formato
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>
      </Row>

      {/* Fila de KPIs */}
      <Row className="mb-4">
        <Col md={4}>
          <KpiCard
            icon={<FaUsers />}
            title="Contactos Totales"
            value={contacts.length}
            color="primary"
          />
        </Col>
        <Col md={4}>
          <KpiCard
            icon={<FaBuilding />}
            title="Empresas"
            value={new Set(contacts.map((c) => c.transportista)).size}
            color="info"
          />
        </Col>
        <Col md={4}>
          <KpiCard
            icon={<FaCheckCircle />}
            title="Contactos Activos"
            value={contacts.length}
            color="success"
          />
        </Col>
      </Row>

      <Card className="shadow-sm rounded-4 border-0">
        <Card.Header className="bg-white p-3 border-bottom-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold">
            <FaUsers className="me-2 text-primary" />
            Todos los Contactos
          </h5>
          <InputGroup style={{width: "300px"}}>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Buscar por nombre, empresa, RUT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Header>
        <Card.Body>
          <Table hover responsive className="align-middle">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Apellido</th>
                <th>Empresa</th>
                <th>RUT</th>
                <th>Correo Electrónico</th>
                <th>Nro. Telefónico</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((contact) => (
                <tr key={contact.id}>
                  <td>{contact.firstName}</td>
                  <td>{contact.lastName}</td>
                  <td>{contact.transportista}</td>
                  <td>{contact.rut}</td>
                  <td>{contact.email}</td>
                  <td>{contact.phone}</td>
                  <td className="text-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      title="Editar"
                      onClick={() => handleShowEditModal(contact)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      title="Eliminar"
                      onClick={() => handleDeleteClick(contact)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        {totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-center">
            <Pagination>{renderPaginationItems()}</Pagination>
          </Card.Footer>
        )}
      </Card>

      {/* --- Modal para Añadir/Editar Contacto --- */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Form onSubmit={handleSaveContact}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingContact ? "Editar Contacto" : "Añadir Nuevo Contacto"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                name="firstName"
                placeholder="Ingrese el nombre"
                value={formState.firstName || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Apellido</Form.Label>
              <Form.Control
                type="text"
                name="lastName"
                placeholder="Ingrese el apellido"
                value={formState.lastName || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>RUT</Form.Label>
              <Form.Control
                type="text"
                name="rut"
                placeholder="12.345.678-9"
                value={formState.rut || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Correo Electrónico</Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="ejemplo@correo.com"
                value={formState.email || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Número Telefónico</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                placeholder="+569..."
                value={formState.phone || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Empresa Transportista</Form.Label>
              <Form.Control
                type="text"
                name="transportista"
                placeholder="Escribe para buscar o añadir una nueva empresa..."
                value={formState.transportista || ""}
                onChange={handleFormChange}
                required
                list="organizations-list"
              />
              <datalist id="organizations-list">
                {uniqueOrganizations.sort().map((orgName) => (
                  <option key={orgName} value={orgName} />
                ))}
              </datalist>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cerrar
            </Button>
            <Button
              type="submit"
              style={{
                backgroundColor: "#20c997",
                borderColor: "#20c997",
                color: "white",
              }}
            >
              {editingContact ? "Actualizar" : "Guardar Contacto"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de Confirmación para Eliminar Contacto */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que quieres eliminar a{" "}
          <strong>
            {contactToDelete?.firstName} {contactToDelete?.lastName}
          </strong>
          ? Esta acción no se puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Sí, Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Contacts;
